import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { fetchWithAuth } from '../utils/api';
import { Activity, Building, Users, FileText, AlertTriangle, Calendar, ChevronDown, ChevronRight, X, BarChart3 } from 'lucide-react';
import toast from 'react-hot-toast';
import { StatCard, Widget } from './Common';
import { SuperAdminUsageTab } from './UsageMonitor';

function SuperAdminView() {
  const { organization } = useContext(AuthContext);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOrgs, setExpandedOrgs] = useState({});
  const [adminTab, setAdminTab] = useState('organizations');

  useEffect(() => {
    loadAllOrganizations();
  }, []);

  const loadAllOrganizations = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth('/admin/organization', organization?.organizationId);
      setOrganizations(data.organizations || []);
    } catch (error) {
      console.error('Error loading organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const toggleOrg = (orgId) => {
    setExpandedOrgs(prev => ({
      ...prev,
      [orgId]: !prev[orgId]
    }));
  };

  const filteredOrgs = organizations.filter(org => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      org.organization_id.toLowerCase().includes(searchLower) ||
      org.organization_name?.toLowerCase().includes(searchLower) ||
      org.users?.some(user =>
        user.email.toLowerCase().includes(searchLower) ||
        user.name?.toLowerCase().includes(searchLower)
      )
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Activity className="w-8 h-8 text-blue-500 animate-spin mr-3" />
        <p className="text-gray-600">Loading organizations...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Tab Bar ── */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setAdminTab('organizations')}
            className={`${
              adminTab === 'organizations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <Building className="w-4 h-4" />
            <span>Organizations</span>
          </button>
          <button
            onClick={() => setAdminTab('usage')}
            className={`${
              adminTab === 'usage'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <BarChart3 className="w-4 h-4" />
            <span>Usage Monitor</span>
          </button>
        </nav>
      </div>

      {/* ── Organizations Tab ── */}
      {adminTab === 'organizations' && (
        <>
          {/* Stats Dashboard */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <StatCard
              title="Total Organizations"
              value={organizations.length}
              icon={<Building className="w-6 h-6" />}
              color="blue"
              subtitle="Active clients"
            />
            <StatCard
              title="Total Users"
              value={organizations.reduce((sum, org) => sum + (org.users?.length || 0), 0)}
              icon={<Users className="w-6 h-6" />}
              color="green"
              subtitle="All system users"
            />
            <StatCard
              title="Pending Requests"
              value={organizations.reduce((sum, org) =>
                sum + (org.users?.filter(u => u.status === 'pending').length || 0), 0
              )}
              icon={<AlertTriangle className="w-6 h-6" />}
              color="yellow"
              subtitle="Awaiting approval"
            />
            <StatCard
              title="Total Events"
              value={organizations.reduce((sum, org) => sum + (org.event_count || 0), 0)}
              icon={<FileText className="w-6 h-6" />}
              color="purple"
              subtitle="AARs generated"
            />
          </div>

          {/* Organizations List */}
          <Widget
            title="Client Organizations"
            subtitle={`Managing ${organizations.length} organizations • View-only dashboard`}
          >
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by organization, email, or user name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-3 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Info Banner */}
            <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">How to Manage Users</h4>
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Find the organization and user you want to manage</li>
                    <li>Click "Manage in DynamoDB" to open AWS Console</li>
                    <li>Edit the user's <code className="bg-blue-200 px-1 rounded text-xs">status</code> field (pending → approved)</li>
                    <li>Edit the <code className="bg-blue-200 px-1 rounded text-xs">role</code> field if needed (member → admin)</li>
                    <li>Refresh this page to see your changes</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Organizations Table */}
            <div className="space-y-3">
              {filteredOrgs.map((org) => (
                <div key={org.organization_id} className="border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 transition">
                  {/* Organization Header - Clickable */}
                  <button
                    onClick={() => toggleOrg(org.organization_id)}
                    className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-xl shadow-lg">
                        {org.organization_name?.[0]?.toUpperCase() || org.organization_id[0].toUpperCase()}
                      </div>
                      <div className="text-left">
                        <h3 className="font-bold text-gray-900 text-lg">
                          {org.organization_name || org.organization_id}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                          <span className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            {org.users?.length || 0} users
                          </span>
                          <span className="flex items-center">
                            <FileText className="w-4 h-4 mr-1" />
                            {org.event_count || 0} events
                          </span>
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(org.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      {org.users?.filter(u => u.status === 'pending').length > 0 && (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold flex items-center">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {org.users.filter(u => u.status === 'pending').length} Pending
                        </span>
                      )}
                      {expandedOrgs[org.organization_id] ? (
                        <ChevronDown className="w-6 h-6 text-blue-600" />
                      ) : (
                        <ChevronRight className="w-6 h-6 text-blue-600" />
                      )}
                    </div>
                  </button>

                  {/* Expanded User List */}
                  {expandedOrgs[org.organization_id] && (
                    <div className="bg-white border-t-2 border-gray-200">
                      {org.users && org.users.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {org.users
                                .sort((a, b) => {
                                  if (a.status === 'pending' && b.status !== 'pending') return -1;
                                  if (a.status !== 'pending' && b.status === 'pending') return 1;
                                  return new Date(b.created_at) - new Date(a.created_at);
                                })
                                .map((user) => (
                                  <tr key={user.user_id} className={`hover:bg-gray-50 ${user.status === 'pending' ? 'bg-yellow-50' : ''}`}>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold shadow">
                                          {user.email[0].toUpperCase()}
                                        </div>
                                        <div>
                                          <p className="font-medium text-gray-900">{user.name || 'Not Set'}</p>
                                          <p className="text-xs text-gray-500">ID: {user.user_id.substring(0, 8)}...</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                        user.role === 'admin'
                                          ? 'bg-purple-100 text-purple-800'
                                          : 'bg-blue-100 text-blue-800'
                                      }`}>
                                        {user.role?.toUpperCase() || 'MEMBER'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                        user.status === 'approved'
                                          ? 'bg-green-100 text-green-800'
                                          : user.status === 'pending'
                                          ? 'bg-yellow-100 text-yellow-800 animate-pulse'
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                        {user.status?.toUpperCase() || 'UNKNOWN'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <a
                                        href={`https://us-east-1.console.aws.amazon.com/dynamodbv2/home?region=us-east-1#edit-item?itemMode=2&pk=${encodeURIComponent(user.user_id)}&sk=${encodeURIComponent(org.organization_id)}&route=ROUTE_ITEM_EXPLORER&table=aar-application-org-users`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition"
                                      >
                                        Manage in DynamoDB
                                        <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                      </a>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Users className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500">No users in this organization</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* No Results */}
              {filteredOrgs.length === 0 && (
                <div className="text-center py-12">
                  <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg font-medium">No organizations found</p>
                  <p className="text-gray-500 text-sm mt-2">Try adjusting your search terms</p>
                </div>
              )}
            </div>

            {/* Quick Stats Footer */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-blue-600">{filteredOrgs.length}</p>
                  <p className="text-xs text-blue-800 font-medium">Organizations Shown</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-green-600">
                    {filteredOrgs.reduce((sum, org) => sum + (org.users?.filter(u => u.status === 'approved').length || 0), 0)}
                  </p>
                  <p className="text-xs text-green-800 font-medium">Approved Users</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-yellow-600">
                    {filteredOrgs.reduce((sum, org) => sum + (org.users?.filter(u => u.status === 'pending').length || 0), 0)}
                  </p>
                  <p className="text-xs text-yellow-800 font-medium">Pending Approval</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-purple-600">
                    {filteredOrgs.reduce((sum, org) => sum + (org.event_count || 0), 0)}
                  </p>
                  <p className="text-xs text-purple-800 font-medium">Total Events</p>
                </div>
              </div>
            </div>
          </Widget>
        </>
      )}

      {/* ── Usage Monitor Tab ── */}
      {adminTab === 'usage' && <SuperAdminUsageTab />}

    </div>
  );
}

export default SuperAdminView;